import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Avatar,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Stack,
  Chip,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { useDispatch, useSelector } from 'react-redux';
import { updateStaffRequest } from '../store/appActions';
import { getStaffFromToken } from 'src/DecodeToken';
import { getProfile } from 'src/modules/auth/api/authApi';

const ROLES = [
  { label: 'Nurse', value: 'nurse' },
  { label: 'Receptionist', value: 'receptionist' },
  { label: 'Assistant', value: 'assistant' },
];

export default function ProfilePage({ handleClose, onClose }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const staffData = useSelector((state) => state.auth?.staff);

  const isDark = theme.palette.mode === 'dark';
  const closeAction = handleClose || onClose || (() => {});

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'nurse',
    profilePic: '',
  });
  const [imagePreview, setImagePreview] = useState('');

  const selectedClinicStr = localStorage.getItem('selectedClinic');
  let clinicName = 'MyCliniq Dental & Healthcare';
  try {
    if (selectedClinicStr) {
      const parsed = JSON.parse(selectedClinicStr);
      clinicName = parsed.clinicName || clinicName;
    }
  } catch (_) {}

  useEffect(() => {
    if (staffData && staffData.name) {
      setFormData({
        name: staffData.name || '',
        email: staffData.email || '',
        role: staffData.role || 'nurse',
        profilePic: staffData.profilePic || '',
      });
      setImagePreview(staffData.profilePic || '');
    } else {
      const staffName = localStorage.getItem('staffName') || localStorage.getItem('STAFFNAME') || 'Staff Member';
      const email = localStorage.getItem('email') || '';
      const role = localStorage.getItem('role') || 'nurse';
      setFormData({
        name: staffName,
        email: email,
        role: role,
        profilePic: '',
      });

      getProfile()
        .then((res) => {
          if (res?.staff) {
            setFormData({
              name: res.staff.name || staffName,
              email: res.staff.email || email,
              role: res.staff.role || role,
              profilePic: res.staff.profilePic || '',
            });
            setImagePreview(res.staff.profilePic || '');
          }
        })
        .catch((err) => console.log('Profile fetch error:', err));
    }
  }, [staffData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData((prev) => ({ ...prev, profilePic: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    const staffId = getStaffFromToken();
    if (!staffId) {
      console.error('Staff ID not found in token.');
      return;
    }

    setSaving(true);
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('role', formData.role);
    if (formData.profilePic instanceof File) {
      formDataToSend.append('profilePic', formData.profilePic);
    }

    dispatch(
      updateStaffRequest(
        staffId,
        formDataToSend,
        (updatedStaff) => {
          setSaving(false);
          if (updatedStaff?.name) {
            localStorage.setItem('staffName', updatedStaff.name);
            localStorage.setItem('STAFFNAME', updatedStaff.name);
          }
          if (updatedStaff?.role) {
            localStorage.setItem('role', updatedStaff.role);
          }
          setIsEditing(false);
        },
        (error) => {
          setSaving(false);
          console.error('Error updating profile:', error);
        }
      )
    );
  };

  const staffInitial = formData.name ? formData.name.charAt(0).toUpperCase() : 'S';

  const isModal = Boolean(handleClose || onClose);

  const content = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
        borderRadius: isModal ? '0px' : '16px',
        overflow: 'hidden',
        boxShadow: isModal
          ? 'none'
          : isDark
            ? '0 24px 60px rgba(0, 0, 0, 0.8)'
            : '0 24px 60px rgba(15, 23, 42, 0.1)',
        border: isModal ? 'none' : `1px solid ${theme.palette.divider}`,
        maxWidth: isModal ? '100%' : '560px',
      }}
    >
      {/* Modal Header */}
      <Box
        sx={{
          py: 2.2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? alpha(theme.palette.background.default, 0.6) : '#F8FAFC',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', color: theme.palette.text.primary, lineHeight: 1.2 }}>
              {isEditing ? 'Edit Profile' : 'Staff Profile'}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
              {isEditing ? 'Update account details and preferences' : 'Staff Desk & Account Information'}
            </Typography>
          </Box>
        </Box>

        {isModal && (
          <IconButton
            onClick={closeAction}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              borderRadius: '10px',
              p: 0.8,
              '&:hover': {
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                color: theme.palette.text.primary,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
        {!isEditing ? (
          /* View Mode */
          <Stack spacing={2.5} alignItems="center">
            {/* Avatar & Badges */}
            <Box sx={{ position: 'relative', textAlign: 'center', my: 0.5 }}>
              <Avatar
                src={imagePreview}
                sx={{
                  width: 86,
                  height: 86,
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText || '#FFFFFF',
                  fontSize: '32px',
                  fontWeight: 800,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                  mx: 'auto',
                  border: `3px solid ${theme.palette.background.paper}`,
                }}
              >
                {staffInitial}
              </Avatar>

              <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.text.primary, mt: 1.5, mb: 0.5, fontSize: '18px' }}>
                {formData.name || 'Staff Member'}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Chip
                  label={(formData.role || 'Staff').toUpperCase()}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '11px',
                    height: 22,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    borderRadius: '6px',
                    letterSpacing: '0.5px',
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22C55E' }} />
                  <Typography variant="caption" sx={{ color: '#22C55E', fontWeight: 600, fontSize: '11px' }}>
                    Active
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Profile Info Cards */}
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              <Box
                sx={{
                  p: 1.8,
                  borderRadius: '12px',
                  bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EmailIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '11px', fontWeight: 500 }}>
                    Email Address
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '13.5px', wordBreak: 'break-word' }}>
                    {formData.email || '—'}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  p: 1.8,
                  borderRadius: '12px',
                  bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BadgeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '11px', fontWeight: 500 }}>
                    Department / Role
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, textTransform: 'capitalize', fontSize: '13.5px' }}>
                    {formData.role || 'Staff'} Desk
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  p: 1.8,
                  borderRadius: '12px',
                  bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LocalHospitalIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '11px', fontWeight: 500 }}>
                    Assigned Clinic
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '13.5px', wordBreak: 'break-word' }}>
                    {clinicName}
                  </Typography>
                </Box>
              </Box>
            </Stack>

            {/* Action Buttons */}
            <Button
              variant="contained"
              fullWidth
              startIcon={<EditIcon sx={{ fontSize: 18 }} />}
              onClick={() => setIsEditing(true)}
              sx={{
                py: 1.1,
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '14px',
                textTransform: 'none',
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText || '#FFFFFF',
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              Edit Profile Details
            </Button>
          </Stack>
        ) : (
          /* Edit Mode */
          <Stack spacing={2.5}>
            {/* Avatar Picker */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={imagePreview}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText || '#FFFFFF',
                    fontSize: '28px',
                    fontWeight: 800,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  {staffInitial}
                </Avatar>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  id="profile-picture-upload"
                />
                <label htmlFor="profile-picture-upload">
                  <IconButton
                    component="span"
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      bgcolor: theme.palette.primary.main,
                      color: '#FFFFFF',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      p: 0.7,
                      '&:hover': { bgcolor: theme.palette.primary.dark },
                    }}
                  >
                    <CameraAltIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </label>
              </Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11px' }}>
                Click camera icon to change photo
              </Typography>
            </Box>

            {/* Form Fields */}
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                  Full Name
                </Typography>
                <TextField
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '10px',
                      bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                      fontSize: '14px',
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                  Email Address
                </Typography>
                <TextField
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '10px',
                      bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                      fontSize: '14px',
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8, color: theme.palette.text.primary, fontSize: '13.5px' }}>
                  Staff Role
                </Typography>
                <TextField
                  select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    sx: {
                      borderRadius: '10px',
                      bgcolor: isDark ? alpha(theme.palette.background.default, 0.4) : '#F9FAFB',
                      fontSize: '14px',
                    },
                  }}
                >
                  {ROLES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            <Divider sx={{ borderColor: theme.palette.divider, my: 0.5 }} />

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setIsEditing(false)}
                sx={{
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '14px',
                  px: 2.5,
                  py: 0.9,
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.text.primary,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
                disabled={saving}
                onClick={handleSave}
                sx={{
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '14px',
                  px: 3,
                  py: 0.9,
                  textTransform: 'none',
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText || '#FFFFFF',
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );

  if (!isModal) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', justifyContent: 'center', width: '100%' }}>
        {content}
      </Box>
    );
  }

  return content;
}
